import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Bell, Check, CheckCheck, Mail, MailOpen, Trash2, Reply, PenSquare, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const Notifications = ({ user }) => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [replyDialog, setReplyDialog] = useState({ open: false, notif: null });
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [newMsgDialog, setNewMsgDialog] = useState(false);
  const [newMsgData, setNewMsgData] = useState({ sujet: '', contenu: '' });
  const [centreUsers, setCentreUsers] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');

  const isCentreOrRP = user?.role === 'centre' || user?.role === 'responsable_pedagogique';

  useEffect(() => {
    loadNotifications();
    loadRecipients();
  }, []);

  const loadRecipients = async () => {
    try {
      if (isCentreOrRP) {
        // Centre/RP can message anyone
        const roles = ['formateur', 'tuteur', 'entreprise', 'stagiaire', 'responsable_pedagogique', 'centre'];
        const results = await Promise.all(
          roles.map(r => axios.get(`/users-by-role/${r}`).catch(() => ({ data: [] })))
        );
        const all = results.flatMap(r => r.data || []).filter(u => u.user_id !== user?.user_id);
        setCentreUsers(all);
        if (all.length > 0) setSelectedRecipient(all[0].user_id);
      } else {
        // Others can only message centre/RP
        const [centreRes, rpRes] = await Promise.all([
          axios.get('/users-by-role/centre').catch(() => ({ data: [] })),
          axios.get('/users-by-role/responsable_pedagogique').catch(() => ({ data: [] }))
        ]);
        const all = [...(centreRes.data || []), ...(rpRes.data || [])];
        setCentreUsers(all);
        if (all.length > 0) setSelectedRecipient(all[0].user_id);
      }
    } catch {}
  };

  const handleSendNewMessage = async () => {
    if (!newMsgData.contenu.trim()) { toast.error('Veuillez saisir un message'); return; }
    if (!selectedRecipient) { toast.error('Veuillez sélectionner un destinataire'); return; }
    setSendingReply(true);
    try {
      await axios.post('/messages/send', {
        to_user_id: selectedRecipient,
        sujet: newMsgData.sujet,
        contenu: newMsgData.contenu
      });
      toast.success('Message envoyé');
      setNewMsgDialog(false);
      setNewMsgData({ sujet: '', contenu: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingReply(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await axios.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`/notifications/${notificationId}/read`);
      setNotifications(
        notifications.map((n) =>
          n.notification_id === notificationId ? { ...n, lu: true } : n
        )
      );
    } catch (error) {
    }
  };

  const markAllAsRead = async () => {
    try {
      const unread = notifications.filter(n => !n.lu);
      await Promise.all(unread.map(n => axios.put(`/notifications/${n.notification_id}/read`)));
      setNotifications(notifications.map(n => ({ ...n, lu: true })));
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`/notifications/${notificationId}`);
      setNotifications(notifications.filter(n => n.notification_id !== notificationId));
      toast.success('Notification supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const deleteAllNotifications = async () => {
    if (!window.confirm('Supprimer toutes les notifications ?')) return;
    try {
      const res = await axios.delete('/notifications');
      setNotifications([]);
      toast.success(res.data.message);
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) { toast.error('Veuillez saisir un message'); return; }
    setSendingReply(true);
    try {
      // Find the centre user to reply to
      const centres = await axios.get('/users-by-role/centre');
      const centreId = centres.data?.[0]?.user_id;
      if (!centreId) { toast.error('Centre non trouvé'); return; }
      await axios.post('/messages/reply', {
        to_user_id: centreId,
        sujet: `Re: ${replyDialog.notif?.titre || ''}`,
        contenu: replyContent
      });
      toast.success('Réponse envoyée au Centre');
      setReplyDialog({ open: false, notif: null });
      setReplyContent('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingReply(false);
    }
  };

  const filteredNotifications = notifications.filter((n) => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.lu;
    if (filter === 'autonomie') return n.type?.includes('autonomie');
    return n.type === filter;
  });

  const unreadCount = notifications.filter(n => !n.lu).length;

  const getNotificationColor = (type) => {
    if (type?.includes('autonomie')) return 'border-l-purple-500 bg-purple-50/30';
    if (type?.includes('note') || type?.includes('competence')) return 'border-l-blue-500 bg-blue-50/30';
    if (type?.includes('epmsp') || type?.includes('certificat')) return 'border-l-green-500 bg-green-50/30';
    if (type?.includes('lettre_mission')) return 'border-l-amber-500 bg-amber-50/30';
    if (type?.includes('refus') || type?.includes('refuse')) return 'border-l-red-500 bg-red-50/30';
    if (type?.includes('declaration')) return 'border-l-teal-500 bg-teal-50/30';
    return 'border-l-slate-400 bg-slate-50/30';
  };

  const getTypeLabel = (type) => {
    if (type?.includes('autonomie')) return 'Autonomie';
    if (type?.includes('note')) return 'Note';
    if (type?.includes('competence')) return 'Compétence';
    if (type?.includes('epmsp') || type?.includes('certificat')) return 'EPMSP';
    if (type?.includes('lettre_mission')) return 'Lettre de Mission';
    if (type?.includes('presence')) return 'Présence';
    if (type?.includes('declaration')) return 'Déclaration';
    if (type?.includes('document')) return 'Document';
    return 'Notification';
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-1">Messagerie</h1>
          <p className="text-slate-600">
            {unreadCount > 0 ? (
              <span className="text-red-600 font-medium">{unreadCount} non lue{unreadCount > 1 ? 's' : ''}</span>
            ) : (
              'Tous les messages sont lus'
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {centreUsers.length > 0 && (
            <Button
              onClick={() => setNewMsgDialog(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="new-message-btn"
            >
              <PenSquare className="h-4 w-4 mr-2" />
              Nouveau message
            </Button>
          )}
          {unreadCount > 0 && (
            <Button
              onClick={markAllAsRead}
              className="bg-[#8DC63F] hover:bg-[#7db336] text-white"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Tout marquer comme lu
            </Button>
          )}
          {notifications.length > 0 && (
            <Button
              variant="outline"
              onClick={deleteAllNotifications}
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="delete-all-notifs-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Tout supprimer
            </Button>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'Toutes' },
            { value: 'unread', label: `Non lues (${unreadCount})` },
            { value: 'autonomie', label: 'Autonomies' },
            { value: 'note', label: 'Notes' },
            { value: 'competence', label: 'Compétences' },
            { value: 'presence', label: 'Présence' }
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f.value
                  ? 'bg-[#8DC63F] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-2">
        {filteredNotifications.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center shadow-sm">
            <Bell className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">Aucun message</p>
          </div>
        ) : (
          filteredNotifications.map((notif) => (
            <div
              key={notif.notification_id}
              className={`rounded-lg border-l-4 shadow-sm transition-all ${
                notif.lu
                  ? 'bg-white border border-slate-200 border-l-slate-300 opacity-70'
                  : `${getNotificationColor(notif.type)} border border-slate-200`
              }`}
              data-testid={`notification-${notif.notification_id}`}
            >
              <div className="flex items-start p-4 gap-3">
                {/* Icône lu/non lu */}
                <div className={`mt-0.5 shrink-0 ${notif.lu ? 'text-slate-400' : 'text-[#8DC63F]'}`}>
                  {notif.lu ? <MailOpen className="h-5 w-5" /> : <Mail className="h-5 w-5" />}
                </div>

                {/* Contenu */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className={`font-semibold text-[#0F172A] ${!notif.lu ? 'text-base' : 'text-sm'}`}>
                          {notif.titre}
                        </h3>
                        <span className={`shrink-0 px-2 py-0.5 text-xs rounded-full font-medium ${
                          notif.lu ? 'bg-slate-100 text-slate-500' : 'bg-[#8DC63F]/15 text-[#6a9a2e]'
                        }`}>
                          {getTypeLabel(notif.type)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-1">{notif.message}</p>
                      {notif.lien && (
                        <Link to={notif.lien} className="mb-1 inline-block text-xs font-semibold text-[#0E7C74] hover:underline" data-testid={`notification-lien-${notif.notification_id}`}>
                          {notif.lien_label || 'Ouvrir'}
                        </Link>
                      )}
                      <p className="text-xs text-slate-400">
                        {new Date(notif.created_at || notif.date).toLocaleDateString('fr-FR', {
                          day: 'numeric', month: 'long', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Bouton Répondre (pour messages du centre) */}
                      {(notif.type === 'message_centre' || notif.type === 'message_reponse') && user?.role !== 'centre' && (
                        <button
                          onClick={() => { setReplyDialog({ open: true, notif }); setReplyContent(''); }}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                          title="Répondre"
                          data-testid={`reply-${notif.notification_id}`}
                        >
                          <Reply className="h-4 w-4" />
                        </button>
                      )}
                      {/* Bouton marquer comme lu */}
                      {!notif.lu && (
                        <Button
                          onClick={() => markAsRead(notif.notification_id)}
                          size="sm"
                          className="shrink-0 bg-[#0F172A] hover:bg-slate-700 text-white"
                          data-testid={`mark-read-${notif.notification_id}`}
                        >
                          <Check className="h-3.5 w-3.5 mr-1" />
                          Lu
                        </Button>
                      )}
                      {notif.lu && (
                        <span className="shrink-0 text-xs text-slate-400 italic mr-1">Lu</span>
                      )}
                      {/* Bouton Supprimer */}
                      <button
                        onClick={() => deleteNotification(notif.notification_id)}
                        className="p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 rounded transition-colors"
                        title="Supprimer"
                        data-testid={`delete-${notif.notification_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Reply Dialog */}
      {replyDialog.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setReplyDialog({ open: false, notif: null })}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()} data-testid="reply-dialog">
            <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Répondre au Centre</h3>
            <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm text-slate-600">
              <p className="font-medium text-slate-700 mb-1">{replyDialog.notif?.titre}</p>
              <p>{replyDialog.notif?.message}</p>
            </div>
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Votre réponse..."
              className="w-full h-32 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#8DC63F] mb-4"
              data-testid="reply-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReplyDialog({ open: false, notif: null })} data-testid="reply-cancel-btn">
                Annuler
              </Button>
              <Button
                onClick={handleReply}
                disabled={sendingReply}
                className="bg-[#8DC63F] hover:bg-[#7db336] text-white"
                data-testid="reply-send-btn"
              >
                {sendingReply ? 'Envoi...' : 'Envoyer la réponse'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* New Message Dialog */}
      {newMsgDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setNewMsgDialog(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()} data-testid="new-message-dialog">
            <h3 className="text-lg font-semibold text-[#0F172A] mb-4">Nouveau message</h3>
            {centreUsers.length > 0 && (
              <div className="mb-3">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Destinataire</label>
                <select
                  value={selectedRecipient}
                  onChange={e => setSelectedRecipient(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                  data-testid="msg-recipient-select"
                >
                  {centreUsers.map(u => {
                    const roleLabels = { centre: 'Centre', formateur: 'Formateur', tuteur: 'Tuteur', entreprise: 'Entreprise', stagiaire: 'Stagiaire', responsable_pedagogique: 'RP' };
                    return (
                      <option key={u.user_id} value={u.user_id}>
                        {u.prenom} {u.nom} ({roleLabels[u.role] || u.role})
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            <div className="mb-3">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Sujet</label>
              <input
                value={newMsgData.sujet}
                onChange={e => setNewMsgData(d => ({ ...d, sujet: e.target.value }))}
                placeholder="Sujet du message..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                data-testid="msg-subject-input"
              />
            </div>
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Message</label>
              <textarea
                value={newMsgData.contenu}
                onChange={e => setNewMsgData(d => ({ ...d, contenu: e.target.value }))}
                placeholder="Votre message..."
                className="w-full h-32 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                data-testid="msg-content-textarea"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNewMsgDialog(false)} data-testid="msg-cancel-btn">
                Annuler
              </Button>
              <Button
                onClick={handleSendNewMessage}
                disabled={sendingReply}
                className="bg-[#8DC63F] hover:bg-[#7db336] text-white"
                data-testid="msg-send-btn"
              >
                <Send className="h-4 w-4 mr-1" />
                {sendingReply ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
